import {
  HomeAccelerationCardCmsContent,
  HomeFeatureCardCmsContent,
  HomePageCmsContent,
  HomeTechnologyBenefitsCardCmsContent,
} from './interfaces'
import { CMS_BASE_URL, CMS_CACHE_CONFIG, CMS_ENDPOINTS } from './constants'
import { readBrowserCmsCache, writeBrowserCmsCache } from './cache/browserCmsCache'
import {
  extractStrapiMediaUrl,
  isAbortError,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

interface FetchHomeContentOptions {
  signal?: AbortSignal
}

const toOptionalString = (value: unknown): string | undefined => {
  const stringValue = readString(value)

  return stringValue === null ? undefined : stringValue
}

const toOptionalMediaUrl = (value: unknown): string | undefined => {
  const mediaUrl = extractStrapiMediaUrl(value, CMS_BASE_URL)

  return mediaUrl === null ? undefined : mediaUrl
}

const mapHomePageContent = (payload: unknown): HomePageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const hero = toStrapiEntity(contentRoot.hero)
  const soundcore = toStrapiEntity(contentRoot.soundcore)

  if (!hero || !soundcore) {
    return null
  }

  const heroPillText = readString(hero.pillText)
  const heroTitleText = readString(hero.titleText)
  const heroDescription = readString(hero.description)
  const heroBackgroundImageUrl = extractStrapiMediaUrl(hero.backgroundImage, CMS_BASE_URL)
  const heroBackgroundVideoUrl = toOptionalString(hero.backgroundVideoUrl)

  const soundcoreLogoUrl = extractStrapiMediaUrl(soundcore.logo, CMS_BASE_URL)
  const soundcoreText = readString(soundcore.text)
  const soundcoreButtonText = readString(soundcore.buttonText)
  const soundcoreButtonUrl = readString(soundcore.buttonUrl)
  const soundcoreDesktopBackgroundUrl = extractStrapiMediaUrl(soundcore.desktopBackground, CMS_BASE_URL)
  const soundcoreMobileBackgroundUrl = extractStrapiMediaUrl(soundcore.mobileBackground, CMS_BASE_URL)
  const soundcoreFeatureCards = toStrapiEntityCollection(soundcore.featureCards)
    .map((card): HomeFeatureCardCmsContent | null => {
      const text = readString(card.text)
      const iconUrl = extractStrapiMediaUrl(card.icon, CMS_BASE_URL)

      if (text === null || iconUrl === null) {
        return null
      }

      return { text, iconUrl }
    })
    .filter((card): card is HomeFeatureCardCmsContent => card !== null)

  const accelerationTitle = readString(contentRoot.accelerationTitle)
  const accelerationFeatureCards = toStrapiEntityCollection(contentRoot.accelerationFeatureCards)
    .map((card): HomeAccelerationCardCmsContent | null => {
      const title = readString(card.title)
      const iconUrl = extractStrapiMediaUrl(card.icon, CMS_BASE_URL)

      if (title === null || iconUrl === null) {
        return null
      }

      return { title, iconUrl }
    })
    .filter((card): card is HomeAccelerationCardCmsContent => card !== null)
  const accelerationStatCardRoot = toStrapiEntity(contentRoot.accelerationStatCard)
  let accelerationStatCard: HomeAccelerationCardCmsContent | null = null

  if (accelerationStatCardRoot) {
    const statValue = readString(accelerationStatCardRoot.statValue)
    const body = readString(accelerationStatCardRoot.body)

    if (statValue !== null && body !== null) {
      accelerationStatCard = {
        body,
        iconUrl: toOptionalMediaUrl(accelerationStatCardRoot.icon),
        statPrefix: toOptionalString(accelerationStatCardRoot.statPrefix),
        statValue,
        statSuffix: toOptionalString(accelerationStatCardRoot.statSuffix),
      }
    }
  }

  let accelerationCards = accelerationFeatureCards

  if (accelerationStatCard !== null) {
    accelerationCards = [
      ...accelerationFeatureCards.slice(0, 2),
      accelerationStatCard,
      ...accelerationFeatureCards.slice(2),
    ]
  }

  const soundscapeLogoUrl = extractStrapiMediaUrl(contentRoot.soundscapeLogo, CMS_BASE_URL)
  const soundscapeText = readString(contentRoot.soundscapeText)
  const soundscapeButtonText = readString(contentRoot.soundscapeButtonText)
  const soundscapeButtonUrl = readString(contentRoot.soundscapeButtonUrl)
  const soundscapeDesktopBackgroundUrl = extractStrapiMediaUrl(contentRoot.soundscapeDesktopBackground, CMS_BASE_URL)
  const soundscapeMobileBackgroundUrl = extractStrapiMediaUrl(contentRoot.soundscapeMobileBackground, CMS_BASE_URL)
  const soundscapeFeatureCards = toStrapiEntityCollection(contentRoot.soundscapeFeatureCards)
    .map((card): HomeFeatureCardCmsContent | null => {
      const text = readString(card.text)
      const iconUrl = extractStrapiMediaUrl(card.icon, CMS_BASE_URL)

      if (text === null || iconUrl === null) {
        return null
      }

      return { text, iconUrl }
    })
    .filter((card): card is HomeFeatureCardCmsContent => card !== null)

  const partnerProgramTitle = readString(contentRoot.partnerProgramTitle)
  const partnerProgramSubtitle = readString(contentRoot.partnerProgramSubtitle)
  const partnerProgramPillItems = toStrapiEntityCollection(contentRoot.partnerProgramPillItems)
    .map((item) => readString(item.text))
    .filter((item): item is string => item !== null)
  const partnerProgramButtonText = readString(contentRoot.partnerProgramButtonText)
  const partnerProgramButtonUrl = readString(contentRoot.partnerProgramButtonUrl)

  const technologyBenefitsTitle = readString(contentRoot.technologyBenefitsTitle)
  const technologyBenefitsDescription = readString(contentRoot.technologyBenefitsDescription)
  const technologyBenefitsCards = toStrapiEntityCollection(contentRoot.technologyBenefitsCards)
    .map((card): HomeTechnologyBenefitsCardCmsContent | null => {
      const title = readString(card.title)
      const body = readString(card.body)

      if (title === null || body === null) {
        return null
      }

      return { title, body }
    })
    .filter((card): card is HomeTechnologyBenefitsCardCmsContent => card !== null)

  const bottomHeroTitleLine1 = readString(contentRoot.bottomHeroTitleLine1)
  const bottomHeroTitleLine2 = toOptionalString(contentRoot.bottomHeroTitleLine2)
  const bottomHeroDescription = toOptionalString(contentRoot.bottomHeroDescription)
  const bottomHeroButtonText = readString(contentRoot.bottomHeroButtonText)
  const bottomHeroButtonUrl = readString(contentRoot.bottomHeroButtonUrl)

  if (
    heroPillText === null ||
    heroTitleText === null ||
    heroDescription === null ||
    heroBackgroundImageUrl === null ||
    soundcoreLogoUrl === null ||
    soundcoreText === null ||
    soundcoreButtonText === null ||
    soundcoreButtonUrl === null ||
    soundcoreDesktopBackgroundUrl === null ||
    soundcoreMobileBackgroundUrl === null ||
    soundcoreFeatureCards.length === 0 ||
    accelerationTitle === null ||
    accelerationFeatureCards.length !== 4 ||
    accelerationStatCard === null ||
    accelerationCards.length !== 5 ||
    soundscapeLogoUrl === null ||
    soundscapeText === null ||
    soundscapeButtonText === null ||
    soundscapeButtonUrl === null ||
    soundscapeDesktopBackgroundUrl === null ||
    soundscapeMobileBackgroundUrl === null ||
    soundscapeFeatureCards.length !== 3 ||
    partnerProgramTitle === null ||
    partnerProgramSubtitle === null ||
    partnerProgramPillItems.length === 0 ||
    partnerProgramButtonText === null ||
    partnerProgramButtonUrl === null ||
    technologyBenefitsTitle === null ||
    technologyBenefitsDescription === null ||
    technologyBenefitsCards.length !== 3 ||
    bottomHeroTitleLine1 === null ||
    bottomHeroButtonText === null ||
    bottomHeroButtonUrl === null
  ) {
    return null
  }

  return {
    hero: {
      pillText: heroPillText,
      titleText: heroTitleText,
      description: heroDescription,
      backgroundImageUrl: heroBackgroundImageUrl,
      backgroundVideoUrl: heroBackgroundVideoUrl,
    },
    soundcore: {
      logoUrl: soundcoreLogoUrl,
      text: soundcoreText,
      buttonText: soundcoreButtonText,
      buttonUrl: soundcoreButtonUrl,
      desktopBackgroundUrl: soundcoreDesktopBackgroundUrl,
      mobileBackgroundUrl: soundcoreMobileBackgroundUrl,
      featureCards: soundcoreFeatureCards,
    },
    acceleration: {
      title: accelerationTitle,
      cards: accelerationCards,
    },
    soundscape: {
      logoUrl: soundscapeLogoUrl,
      text: soundscapeText,
      buttonText: soundscapeButtonText,
      buttonUrl: soundscapeButtonUrl,
      desktopBackgroundUrl: soundscapeDesktopBackgroundUrl,
      mobileBackgroundUrl: soundscapeMobileBackgroundUrl,
      featureCards: soundscapeFeatureCards,
    },
    partnerProgram: {
      title: partnerProgramTitle,
      subtitle: partnerProgramSubtitle,
      pillItems: partnerProgramPillItems,
      buttonText: partnerProgramButtonText,
      buttonUrl: partnerProgramButtonUrl,
    },
    technologyBenefits: {
      title: technologyBenefitsTitle,
      description: technologyBenefitsDescription,
      cards: technologyBenefitsCards,
    },
    bottomHero: {
      titleLine1: bottomHeroTitleLine1,
      titleLine2: bottomHeroTitleLine2,
      description: bottomHeroDescription,
      buttonText: bottomHeroButtonText,
      buttonUrl: bottomHeroButtonUrl,
    },
  }
}

export const fetchHomeContentFromStrapi = async (
  options: FetchHomeContentOptions = {},
): Promise<HomePageCmsContent | null> => {
  const response = await fetch(`${CMS_BASE_URL}${CMS_ENDPOINTS.homePage}`, {
    method: 'GET',
    signal: options.signal,
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Unable to fetch home CMS content (${response.status})`)
  }

  const payload = await response.json() as unknown

  return mapHomePageContent(payload)
}

export const loadHomeContentWithFallback = async (
  options: FetchHomeContentOptions = {},
): Promise<HomePageCmsContent | null> => {
  const cacheConfig = CMS_CACHE_CONFIG.homePage

  try {
    const freshContent = await fetchHomeContentFromStrapi(options)

    if (freshContent) {
      writeBrowserCmsCache({
        key: cacheConfig.key,
        version: cacheConfig.version,
        ttlMs: cacheConfig.ttlMs,
        payload: freshContent,
      })
      return freshContent
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw error
    }
  }

  return readBrowserCmsCache({
    key: cacheConfig.key,
    version: cacheConfig.version,
  })
}
